import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { MeResp } from '@postmaker/shared';
import { zMeResp } from '@postmaker/shared';
import { api, renovarSesion } from './lib/api.ts';
import { CLAVE_SESION } from './lib/sesion.ts';
import { aplicarBranding } from './lib/branding.ts';
import { Encabezado } from './componentes/Encabezado.tsx';
import { PieDePagina } from './componentes/PieDePagina.tsx';
import { PaginaLogin } from './paginas/Login.tsx';
import { PaginaCambiarClave } from './paginas/CambiarClave.tsx';
import { PaginaRecuperar } from './paginas/Recuperar.tsx';
import { PaginaInicio } from './paginas/Inicio.tsx';
import { PaginaProductos } from './paginas/Productos.tsx';
import { PaginaMiCuenta } from './paginas/MiCuenta.tsx';
import { PaginaPlantillas } from './paginas/Plantillas.tsx';
import { PaginaElegirMarca } from './paginas/ElegirMarca.tsx';
import { PaginaEditorCliente } from './paginas/EditorCliente.tsx';
import { PaginaGaleria } from './paginas/Galeria.tsx';
import { PaginaAdminMarcas } from './paginas/admin/Marcas.tsx';
import { PaginaAdminProductos } from './paginas/admin/Productos.tsx';
import { PaginaAdminClientes } from './paginas/admin/Clientes.tsx';
import { PaginaAdminPlantillas } from './paginas/admin/Plantillas.tsx';
import { PaginaEditorPlantilla } from './paginas/admin/EditorPlantilla.tsx';
import { PaginaAdminMarca } from './paginas/admin/MarcaDetalle.tsx';
import { PaginaAdminCliente } from './paginas/admin/ClienteDetalle.tsx';
import { PaginaAdminCampanias } from './paginas/admin/Campanias.tsx';
import { PaginaAdminAnalitica } from './paginas/admin/Analitica.tsx';
import { PaginaSuperadmin } from './paginas/admin/Superadmin.tsx';
import { PaginaSuperEmpresa } from './paginas/admin/SuperEmpresa.tsx';

interface ContextoRouter {
  queryClient: QueryClient;
}

async function sesionActual(queryClient: QueryClient): Promise<MeResp | null> {
  return queryClient.fetchQuery({
    queryKey: CLAVE_SESION,
    queryFn: async () => {
      const renovado = await renovarSesion();
      if (!renovado) return null;
      const me = await api<MeResp>('/auth/me', { schema: zMeResp });
      aplicarBranding(me.branding);
      return me;
    },
    staleTime: 5 * 60 * 1000,
  });
}

const rutaRaiz = createRootRouteWithContext<ContextoRouter>()({
  component: () => (
    <div className="flex min-h-screen w-full flex-col">
      <Encabezado />
      <div className="flex-1 px-4 py-6 md:px-8">
        <Outlet />
      </div>
      <PieDePagina />
    </div>
  ),
});

const rutaLogin = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/login',
  beforeLoad: async ({ context }) => {
    const sesion = await sesionActual(context.queryClient);
    if (sesion) throw redirect({ to: sesion.usuario.debeCambiarPassword ? '/cambiar-clave' : '/' });
  },
  component: PaginaLogin,
});

const rutaRecuperar = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/recuperar',
  component: PaginaRecuperar,
});

const rutaCambiarClave = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/cambiar-clave',
  beforeLoad: async ({ context }) => {
    const sesion = await sesionActual(context.queryClient);
    if (!sesion) throw redirect({ to: '/login' });
  },
  component: PaginaCambiarClave,
});

const rutaInicio = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/',
  beforeLoad: async ({ context }) => {
    const sesion = await sesionActual(context.queryClient);
    if (!sesion) throw redirect({ to: '/login' });
    // 403 PASSWORD_CHANGE_REQUIRED en la API; aquí se evita el viaje de ida y vuelta.
    if (sesion.usuario.debeCambiarPassword) throw redirect({ to: '/cambiar-clave' });
    // El superadmin no tiene "inicio" de empresa: su casa es la Plataforma.
    if (sesion.usuario.rol === 'SUPERADMIN') throw redirect({ to: '/super' });
  },
  component: PaginaInicio,
});

async function exigirCliente(queryClient: QueryClient) {
  const sesion = await sesionActual(queryClient);
  if (!sesion) throw redirect({ to: '/login' });
  if (sesion.usuario.debeCambiarPassword) throw redirect({ to: '/cambiar-clave' });
}

const rutaProductos = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/productos',
  validateSearch: (s: Record<string, unknown>) => ({
    tipoContenido: typeof s['tipoContenido'] === 'string' ? s['tipoContenido'] : 'PROMOCION',
  }),
  beforeLoad: ({ context }) => exigirCliente(context.queryClient),
  component: PaginaProductos,
});

const rutaElegirMarca = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/marca',
  beforeLoad: ({ context }) => exigirCliente(context.queryClient),
  component: PaginaElegirMarca,
});

const rutaPlantillas = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/plantillas',
  beforeLoad: ({ context }) => exigirCliente(context.queryClient),
  component: PaginaPlantillas,
});

const rutaEditorCliente = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/editor',
  validateSearch: (s: Record<string, unknown>) => ({
    plantillaId: typeof s['plantillaId'] === 'string' ? s['plantillaId'] : '',
  }),
  beforeLoad: ({ context }) => exigirCliente(context.queryClient),
  component: PaginaEditorCliente,
});

const rutaGaleria = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/galeria',
  beforeLoad: ({ context }) => exigirCliente(context.queryClient),
  component: PaginaGaleria,
});

const rutaMiCuenta = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/cuenta',
  beforeLoad: ({ context }) => exigirCliente(context.queryClient),
  component: PaginaMiCuenta,
});

const ROLES_STAFF = new Set(['SUPERADMIN', 'ADMIN_EMPRESA', 'OPERADOR']);

async function exigirStaff(queryClient: QueryClient) {
  const sesion = await sesionActual(queryClient);
  if (!sesion) throw redirect({ to: '/login' });
  if (sesion.usuario.debeCambiarPassword) throw redirect({ to: '/cambiar-clave' });
  if (!ROLES_STAFF.has(sesion.usuario.rol)) throw redirect({ to: '/' });
}

const rutaAdminMarcas = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/marcas',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminMarcas,
});

const rutaAdminMarca = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/marcas/$marcaId',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminMarca,
});

const rutaAdminProductos = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/productos',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminProductos,
});

const rutaAdminClientes = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/clientes',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminClientes,
});

const rutaAdminCliente = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/clientes/$clienteId',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminCliente,
});

const rutaAdminPlantillas = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/plantillas',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminPlantillas,
});

const rutaEditorPlantilla = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/plantillas/$plantillaId',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaEditorPlantilla,
});

const rutaAdminCampanias = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/campanias',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminCampanias,
});

const rutaAdminAnalitica = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/admin/analitica',
  beforeLoad: ({ context }) => exigirStaff(context.queryClient),
  component: PaginaAdminAnalitica,
});

const rutaSuperadmin = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/super',
  beforeLoad: async ({ context }) => {
    const sesion = await sesionActual(context.queryClient);
    if (!sesion) throw redirect({ to: '/login' });
    if (sesion.usuario.rol !== 'SUPERADMIN') throw redirect({ to: '/' });
  },
  component: PaginaSuperadmin,
});

const rutaSuperEmpresa = createRoute({
  getParentRoute: () => rutaRaiz,
  path: '/super/empresas/$empresaId',
  beforeLoad: async ({ context }) => {
    const sesion = await sesionActual(context.queryClient);
    if (!sesion) throw redirect({ to: '/login' });
    if (sesion.usuario.rol !== 'SUPERADMIN') throw redirect({ to: '/' });
  },
  component: PaginaSuperEmpresa,
});

const arbol = rutaRaiz.addChildren([
  rutaLogin,
  rutaRecuperar,
  rutaCambiarClave,
  rutaInicio,
  rutaProductos,
  rutaElegirMarca,
  rutaPlantillas,
  rutaEditorCliente,
  rutaGaleria,
  rutaMiCuenta,
  rutaAdminMarcas,
  rutaAdminProductos,
  rutaAdminClientes,
  rutaAdminPlantillas,
  rutaEditorPlantilla,
  rutaAdminMarca,
  rutaAdminCliente,
  rutaAdminCampanias,
  rutaAdminAnalitica,
  rutaSuperadmin,
  rutaSuperEmpresa,
]);

export function crearRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree: arbol,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultNotFoundComponent: () => (
      <main className="p-8 text-center text-suave">404</main>
    ),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof crearRouter>;
  }
}
